import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Zap, Eye, Receipt, Loader2 } from 'lucide-react';
import recruiterService, { PackRecord } from '@/services/recruiterService';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import { toast } from '@/stores/toastStore';

const MONTHS = [1,2,3,4,5,6,7,8,9,10,11,12];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => CURRENT_YEAR - i);

function formatPrice(cents: number): string {
  return (cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' TND';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function PackHistoryPage() {
  const { t } = useTranslation('recruiter');

  const [packs, setPacks] = useState<PackRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filterMonth, setFilterMonth] = useState<number | ''>('');
  const [filterYear, setFilterYear] = useState<number>(CURRENT_YEAR);

  const fetchPacks = useCallback(async (pageNum: number, append: boolean) => {
    const params: Record<string, number> = { page: pageNum, limit: 20 };
    params.year = filterYear;
    if (filterMonth !== '') {
      params.month = filterMonth;
    }

    append ? setLoadingMore(true) : setLoading(true);
    try {
      const data = await recruiterService.getPackHistory(params);
      const incoming = data.packs ?? [];
      setPacks(prev => append ? [...prev, ...incoming] : incoming);
      setTotalPages(data.pagination?.totalPages ?? 1);
    } catch {
      toast.error(t('packHistory.loadError'));
    } finally {
      append ? setLoadingMore(false) : setLoading(false);
    }
  }, [filterMonth, filterYear, t]);

  // Reset and reload when filter changes
  useEffect(() => {
    setPage(1);
    fetchPacks(1, false);
  }, [filterMonth, filterYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPacks(nextPage, true);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    setFilterMonth(val === '' ? '' : Number(val));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterYear(Number(e.target.value));
  };

  const clearFilter = () => {
    setFilterMonth('');
    setFilterYear(CURRENT_YEAR);
  };

  return (
    <div className="container-custom py-8 max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Receipt className="w-8 h-8 text-blue-600" />
          {t('packHistory.title')}
        </h1>
        <p className="text-gray-600">{t('packHistory.subtitle')}</p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('packHistory.filter.month')}</label>
          <select
            value={filterMonth}
            onChange={handleMonthChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{t('packHistory.filter.allMonths')}</option>
            {MONTHS.map(m => (
              <option key={m} value={m}>
                {new Date(2000, m - 1).toLocaleString(undefined, { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{t('packHistory.filter.year')}</label>
          <select
            value={filterYear}
            onChange={handleYearChange}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {YEARS.map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {(filterMonth !== '' || filterYear !== CURRENT_YEAR) && (
          <Button variant="secondary" size="sm" onClick={clearFilter}>
            {t('packHistory.filter.clear')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        </div>
      ) : packs.length === 0 ? (
        <Card className="text-center py-20">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('packHistory.empty.title')}</h3>
          <p className="text-gray-500">{t('packHistory.empty.subtitle')}</p>
        </Card>
      ) : (
        <>
          <div className="space-y-4">
            {packs.map((pack) => (
              <Card key={pack.id}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <Package className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{pack.planName}</h3>
                      <p className="text-sm text-gray-500">{t('packHistory.purchasedOn', { date: formatDate(pack.purchasedAt) })}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 flex-wrap">
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Zap className="w-4 h-4 text-purple-500" />
                      <span>
                        <span className="font-semibold">{pack.aiMatchesQuota}</span>{' '}
                        {t('packHistory.aiMatches')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span>
                        <span className="font-semibold">{pack.contactRevealsQuota}</span>{' '}
                        {t('packHistory.contactReveals')}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">{formatPrice(pack.pricePaidCents)}</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {page < totalPages && (
            <div className="flex justify-center mt-8">
              <Button variant="secondary" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('packHistory.loadingMore')}
                  </span>
                ) : (
                  t('packHistory.loadMore')
                )}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
